import React from 'react'
import Head from 'next/head'
import { useTranslation } from 'hooks/useTranslation'
import { Faqs } from 'features/Faqs'
import Script from 'next/script'
import { GetServerSideProps } from 'next'

export const getServerSideProps: GetServerSideProps = async () => {
  return {
    redirect: {
      destination: 'https://wefeedraw.gorgias.help/en-US',
      permanent: true,
    },
  }
}

export default function LoginPage() {
  const { t } = useTranslation()

  // prettier-ignore
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
          {
            '@type': 'Question',
            name: 'Is your food complete and balanced?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes. All of our patties are formulated to meet the nutritional levels established by the AAFCO dog food nutrient profiles for all life stages, including the growth of large-breed puppies (70 lbs or more). We Feed Raw is made up of meaty meat (muscle meat and associated connective tissue and fat), organ meat, and bone. There are no fillers, no preservatives, no artificial colors or flavors. We also include a high-quality vitamin and mineral premix for an added boost of nutrition. This premix is exclusively sourced and made in the USA.'
            }
          },
          {
            '@type': 'Question',
            name: 'Is your food AAFCO compliant?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes, our dog and cat foods comply with AAFCO recommendations for all life stages.'
            }
          },
          {
            '@type': 'Question',
            name: 'Were your recipes formulated by a nutritionist?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes, all of our recipes were formulated by a PhD animal nutritionist with over 30 years of experience in raw feeding and animal nutrition.'
            }
          },
          {
            '@type': 'Question',
            name: 'How should I get my dog started?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: "If you sign up for a meal plan, you will be sent our intro plan with detailed instructions. The directions will instruct you on how to mix your pup's current food with We Feed Raw and increase the percentage of We Feed Raw over the course of 9 days until you're feeding 100% WFR. We also highly recommend adding these two products to your dog's food to aid in the transition to raw: plain, canned organic pumpkin (a half a teaspoon for dogs weighing up to 30 lbs, one teaspoon for dogs weighing 30-60 lbs, and one tablespoon for larger dogs) and a pre/probiotic (our favorite is called Love Bugs from Adored Beast Apothecary)."
            }
          },
          {
            '@type': 'Question',
            name: 'Can I feed kibble and We Feed Raw?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: "Yes. While we always recommend feeding 100% raw to see the full benefits of a raw diet, we understand that this isn't for everyone, so we of course support the idea that some fresh raw is better than none."
            }
          },
          {
            '@type': 'Question',
            name: "Should I add anything to my dog's We Feed Raw Meals?",
            acceptedAnswer: {
              '@type': 'Answer',
              text: "Our food is designed to be complete and balanced for dogs of all life stages, so it's not necessary to add anything to your pet's food. We already add a high-quality vitamin premix to the food, so we particularly advise against adding any other multivitamin to the food. That being said, there is no harm in adding a tiny amount of your preferred probiotic, prebiotic, fish oil or omega-3 oil. When it comes to adding other whole foods, like canned pumpkin, pureed greens, phytoplankton, etc., can be great additions if prudent amounts are fed."
            }
          },
          {
            '@type': 'Question',
            name: 'Where do you source your meats from?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Our food is all made from USDA human-grade meats with no fillers, preservatives, artificial flavors or colors. Our lamb and venison are sourced from New Zealand, pasture-raised, grass-fed, and grass-finished. Everything else is sourced within the USA from farms we trust. All of our recipes are blended and packaged in the USA. All We Feed Raw products are produced in a USDA-certified facility with strict compliance requirements of BRC safe quality food manufacturing.'
            }
          },
          {
            '@type': 'Question',
            name: 'What kind of edible bone is in the patties?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'The edible bone is finely ground into the patty. We use turkey necks for the Turkey Patty. We use beef necks for the Beef, Lamb, and Venison Patties. For the Chicken Patty, we use chicken necks and for the Duck Patty, we use duck bones.'
            }
          },
          {
            '@type': 'Question',
            name: 'What safety precautions does WFR take?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: "All We Feed Raw products are produced in a USDA-certified facility with strict compliance requirements of BRC safe quality food manufacturing. Furthermore, as part of our food safety program, we use a cold-pressure process on all of our meats except our treats. This methodology does not use heat, chemicals, non-natural additives, or preservatives, but rather pressure to reduce the presence of pathogenic bacteria. The cold-pressure process is effective at neutralizing Salmonella, E. coli, and Listeria. While we know dogs and cats can handle bacteria in ways that humans can't, cold-pressure offers extra peace of mind while feeding raw in the home."
            }
          },
          {
            '@type': 'Question',
            name: 'Can I cook the food?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: "We don't recommend cooking the food, as this defeats the purpose of feeding a fresh raw diet and all the bioavailable nutrients that come with it. That being said, it's fine to gently sear the meat if you have a picky pet."
            }
          },
          {
            '@type': 'Question',
            name: 'How many days will my food stay fresh?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Our food will stay good in your refrigerator for up to 4 days. Our food will stay good in your freezer sealed in its original packaging for up to 6 months.'
            }
          },
          {
            '@type': 'Question',
            name: "What is the best way to portion my pet's meals?",
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'You can measure out each meal using a regular kitchen scale or just eyeball it. You will always receive the right quantity for your pup, so if you feed a little less or a little more on some days, it will balance out by the time you reach the end of the box.'
            }
          },
          {
            '@type': 'Question',
            name: 'How long does the food take to thaw?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Our meal plan packets take about 12 hours to thaw in the fridge. Our bulk patties take about 24 hours.'
            }
          },
          {
            '@type': 'Question',
            name: 'Is We Feed Raw for cats too?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: "Yes, cats are obligate carnivores, so our recipes are great for them too. We've worked with our PhD nutritionist to ensure that our formulas are complete and balanced for cats as well. We don't add additional taurine to our recipes ﻿because all of them exceed the AAFCO requirement naturally, which is .1% on a dry matter basis for cats of all life stages. Taurine is denatured or deactivated by the heat of canning or extrusion so mandated by AAFCO. Taurine abounds in all raw meat ingredients which in our process are kept chilled throughout and then frozen, never altered in bioavailability. While we currently don't offer meal plans for cats, many of our cat customers purchase our 5 lb bulk patties and portion accordingly."
            }
          },
          {
            '@type': 'Question',
            name: 'Do you ship nationwide?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes. We currently ship to the 48 contiguous states.'
            }
          },
          {
            '@type': 'Question',
            name: 'What is your shipping policy for bulk patties?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'To pass along lower shipping costs and reduce our carbon footprint, bulk patty purchases must be made in 30 lb increments to fill a box. (E.g., 30 lb, 60 lb, 90 lb, 120 lb, etc.) Shipping is free nationwide!'
            }
          },
          {
            '@type': 'Question',
            name: 'Is shipping really free for meal plans and bulk patties?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Is shipping really free? Yep! All We Feed Raw products are cold-shipped for FREE anywhere in the contiguous U.S.'
            }
          },
          {
            '@type': 'Question',
            name: 'How does the food arrive when shipped?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'The food is shipped frozen in an insulated eco-friendly, compostable cooler with dry ice. The food almost always arrives completely frozen. Even if the food arrives partially thawed or refrigerator temperature, it is fine to refreeze and does not affect the quality of the product. As long as one pack is still partially frozen and the rest of the packs are very cold, they are fine to refreeze.'
            }
          },
          {
            '@type': 'Question',
            name: "if I'm not on a subscription, when should I put in a new order?",
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'All orders placed before Saturday at 8:00 pm ET will ship that upcoming Monday, unless Monday is a holiday. If you place your order after Saturday at 8:00 pm ET (Saturday 5:00 pm PT), your order will not ship until the following Monday. Subscription orders are processed very early Friday mornings. Subscription orders will automatically recur based on your chosen shipping frequency so that there is no gap in food.'
            }
          },
          {
            '@type': 'Question',
            name: 'What kind of coolers do you use?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'We use eco-friendly, biodegradable, compostable coolers. Run them under water in your sink and they will dissolve within minutes!'
            }
          },
          {
            '@type': 'Question',
            name: 'Do you have to be home at the time of delivery?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'No. Even if you are not home, the driver will drop off the food. If you plan to have food delivered to your work address, please let us know.'
            }
          },
          {
            '@type': 'Question',
            name: 'What is your cancellation/change policy?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'You can cancel your subscription at any time. Please note that any edits to your subscription must be made before 5:00PM ET on the Thursday before your recurring charge happens. This includes pauses and cancellations. If it is after 5:00PM ET on Thursday and your order has already processed, you must reach out to customer service at hello@wefeedraw.com or text us at 843-732-2849 to request changes or cancel. If you do not reach out to customer service by Saturday 8pm ET, your order cannot be edited, canceled, or refunded.'
            }
          },
          {
            '@type': 'Question',
            name: 'What if I need to change my shipping address at the last minute?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Please reach out to Customer Service at hello@wefeedraw.com before Saturday at 8:00PM ET and we can accommodate your subscription shipping address change.'
            }
          },
          {
            '@type': 'Question',
            name: 'What is your refund policy?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'We will replace/refund customers for lost/damaged boxes/products caused by the carrier or us. We DO NOT refund/replace orders if the customer has moved and has not provided us with a change of address or if you did not cancel before the Saturday 8pm ET cutoff time. Due to the perishable nature of the product, we do not accept returns.'
            }
          },

          {
            "@type": "Question",
            name: "How can I find the tracking information for my order?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "After your order has shipped, we will send an email notification with this information. You can also sign into the My Account section to see tracking information once your order has shipped."
            }
          },
          {
            "@type": "Question",
            name: "How easily can I change my deliveries?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "It's a cinch! Once you're signed up for a subscription, you can make changes to your plan in the My Account section. For example, change addresses, delay or skip an order, or edit your pup's portions and recipe selection."
            }
          },
          {
            "@type": "Question",
            name: "Can I cancel or reactivate my subscription anytime?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes! You can easily pause, cancel or reactivate your subscription anytime. Just sign into the My Account section. Please note that any orders that have already been prepared (24 hours before ship date) cannot be canceled and refunded."
            }
          },
          {
            "@type": "Question",
            name: "How do I get charged while on a subscription?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "You are billed based on your shipping frequency. We notify you via email 3 days before billing/processing all orders."
            }
          },
          {
            "@type": "Question",
            name: "What do I do if I forget my password?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Just click here to create a new one."
            }
          },
          {
            "@type": "Question",
            name: "Why is raw better than \"fresh\", cooked food?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Dogs have short, simple digestive tracts and high levels of hydrochloric acid in their stomachs. They are anatomically designed to derive their nutrients from raw animal protein, fat, and bone. Cooking the food alters its makeup, causing essential nutrients and enzymes to become less bioavailable. In other words, with cooked foods, your dog has to work harder to get the nutrients he needs. With raw, you're feeding food in its most digestible state."
            }
          },
          {
            "@type": "Question",
            name: "Why is raw better for my dog's teeth?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Raw foods contain naturally occurring enzymes that help protect teeth and gums. The starches and sugars found in highly processed foods cause unhealthy bacterial growth. In addition, we recommend feeding recreational bones a few times a week. They act as 'nature's toothbrush.' Not only do they provide mental stimulation and jaw exercise but they naturally clean tartar and plaque from teeth."
            }
          },
          {
            "@type": "Question",
            name: "What if my dog won't eat the food?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "If your dog is new to raw, please know that this is not unusual. The smell, texture, temperature, and flavor are all different than what they're used to. Leave the food out for 20 minutes each mealtime and don't give out any 'yummy treats' (kibble, table scraps, etc.) between meals. If that doesn't work, you can try adding taste toppers to the raw food. Check out other tips for getting picky pups to eat raw food in our blog."
            }
          },
          {
            "@type": "Question",
            name: "What if my cat won't eat the food?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Cats can be much pickier than dogs when switching to a new food. If you have any of your cat's previous food left, you can try mixing it in with the new raw food until your cat gets used to it. You also might want to try feeding the food as close to room temperature as possible. Remember: if your cat was eating dry food or even canned wet food before this, the raw diet will look, smell, taste, and feel a lot different than what they've been conditioned to recognize as food. (Commercial dry foods are sprayed with oils and flavors to make them palatable to cats. They're also loaded with sugars and carbs that cats can become addicted to. Raw diets have no carbs or sugars.) You can always try adding a topper to entice them as well. Some suggestions: powdered liver, shrimp, fish bits, bonito flakes, and/or a sprinkle of parmesan cheese. One important point to remember: be sure your cat doesn't go more than 24 hours without eating, as this can lead to hepatic lipidosis."
            }
          },
          {
            "@type": "Question",
            name: "I've noticed my dog pooping less and that it turns white: is this normal?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, this is completely normal. The reason your dog is pooping less is because most of the food your pet is now ingesting is being digested and properly utilized by the body, resulting in less waste. The bone content in your pet's new diet accounts for it turning white. If you notice your dog straining while pooping, that is normal and even healthy. Straining a little bit can help express the anal glands, which would normally be done by your vet, at a price."
            }
          },
          {
            "@type": "Question",
            name: "Why is my dog's stool covered in mucus at times? Should I be concerned?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Believe it or not, mucus-covered stools can appear at any time, no matter how long a dog has been on the raw diet and is generally no reason for concern. When first switching to a raw diet, this may be a sign that the digestive tract is ridding the junk from its system, which is a normal part of detox. It can also mean an inflammation of the intestinal tract. If you notice traces of blood, along with the mucus-covered stool, a trip to the vet is encouraged. Again, use your judgment. If your dog appears ill (gums may be white and not the normal pink/rose color) and is also having frequent bouts of diarrhea that last 24 to 48 hours, call your vet."
            }
          },
          {
            "@type": "Question",
            name: "Is my dog going through detoxification?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "When switching a pet over from kibble or canned food to We Feed Raw, the dog's body may begin the process of expelling toxins and impurities as it adjusts to the consumption of proper nutrients. This process is called detoxification or simply 'detox'. Depending on factors such as age, how long they have been fed commercial pet food, or immune system health, detox may last a couple of days, one week, one month, several months, or in some cases, not even at all. The most common symptoms of detox include vomiting, diarrhea, bad breath, and itchy skin. Remember to be persistent and know you are going through the transition, which will be the hardest part. Make sure you supply fresh water at all times and, of course, lots of love. For diarrhea and loose stools, you can add some plain organic canned pumpkin, which will help to firm stools. (A half a teaspoon for dogs weighing up to 30 lb, one teaspoon for dogs weighing 30-60 lb, and one tablespoon for larger dogs.)"
            }
          },
          {
            "@type": "Question",
            name: "Is it normal that my pet is drinking less water on the raw diet?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, this is to be expected. We Feed Raw provides food in its raw state. The raw diet is about 70% moisture and is low in sodium. Because kibble has had the water removed, dogs constantly have to drink water to stay properly hydrated."
            }
          },
          {
            "@type": "Question",
            name: "Is there any truth to the idea that my pet will become \"blood thirsty\" from eating raw?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "No, this is a myth. There is no relationship between eating raw meat and wanting to kill animals or people. It has nothing to do with what a dog is fed and has everything to do with natural prey drive, training, and socialization. Dogs are more likely to get aggressive when they are fed an imbalanced diet, because they are uncomfortable and feel ill. Dogs can easily distinguish between what is food and what is not."
            }
          },
        ],
    name: 'FAQs - Everything you need to know about We Feed Raw.',
    url: 'https://wefeedraw.com/faqs'
  };

  const jsonLdString = JSON.stringify(schema)

  return (
    <div>
      <Head>
        <title>{t('faqs:page-title')}</title>
        <meta name="title" content={t('faqs:meta-title')} />
        <meta name="description" content={t('faqs:meta-description')} />
        <link rel="canonical" href="https://wefeedraw.com/faqs" />
      </Head>

      <main>
        <Script
          id="schema-script"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: jsonLdString }}
        />
        <Faqs />
      </main>
    </div>
  )
}
